import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import { Upload, FileText, Target, ArrowLeft } from 'lucide-react';
import domainConfig from './config/domainConfig';
import { generateStandardizedPDF, REPORT_SECTIONS } from './utils/pdfGenerator';
import ReportHeader from './components/ReportHeader';

const PassingTargetsReportGenerator = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.toLowerCase().endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setError(null);
    }
  };

  const handleGenerateReport = async () => {
    if (!file) {
      setError('Please select a CSV file');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('csvFile', file);

      const response = await fetch(`${domainConfig.apiUrl}/api/reports/passingtargets`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate report');
      }

      const data = await response.json();
      setReportData(data.report);
    } catch (error) {
      console.error('Error generating report:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const saveReportAsPDF = async () => {
    await generateStandardizedPDF(
      REPORT_SECTIONS.PASSING_TARGETS,
      'passing-targets-report.pdf',
      setError
    );
  };

  const renderReceiverAnalysis = (analysis) => (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Rank</strong></TableCell>
            <TableCell><strong>Receiver</strong></TableCell>
            <TableCell align="right"><strong>Targets</strong></TableCell>
            <TableCell align="right"><strong>Target %</strong></TableCell>
            <TableCell align="right"><strong>Deep Balls</strong></TableCell>
            <TableCell align="right"><strong>% of Team Deep</strong></TableCell>
            <TableCell align="right"><strong>% Deep of Own</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {analysis.map((receiver) => (
            <TableRow key={receiver.receiver}>
              <TableCell>{receiver.rank}</TableCell>
              <TableCell>
                <strong>{receiver.receiver}</strong>
              </TableCell>
              <TableCell align="right">{receiver.targets}</TableCell>
              <TableCell align="right">{receiver.targetPercentage}%</TableCell>
              <TableCell align="right">{receiver.deepBalls}</TableCell>
              <TableCell align="right">{receiver.deepBallsOfTotal}%</TableCell>
              <TableCell align="right">
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: receiver.deepBallsOfReceiverTargets > 20 ? 'bold' : 'normal',
                    color: receiver.deepBallsOfReceiverTargets > 20 ? 'warning.main' : 'text.primary'
                  }}
                >
                  {receiver.deepBallsOfReceiverTargets}%
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderInsights = (insights) => (
    <Box sx={{ pl: 2 }}>
      {insights.map((insight, index) => (
        <Typography key={index} variant="body1" sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}>
          <span style={{ marginRight: '8px', fontWeight: 'bold' }}>•</span>
          {insight}
        </Typography>
      ))}
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowLeft size={16} />}
          onClick={() => navigate('/reports')}
          variant="outlined"
        >
          Back to Reports
        </Button>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Target size={32} />
          Passing Targets Report
        </Typography>
      </Box>

      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        Analyze passing target distribution and deep ball usage. Upload your CSV file to generate comprehensive receiver analysis.
      </Typography>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* File Upload Section */}
      {!reportData && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Upload size={24} />
              Upload CSV Data
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
                id="csv-file-upload"
              />
              <label htmlFor="csv-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<Upload size={16} />}
                  sx={{ mr: 2 }}
                >
                  Choose CSV File
                </Button>
              </label>
              {file && (
                <Chip
                  label={file.name}
                  onDelete={() => setFile(null)}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>

            <Button
              variant="contained"
              onClick={handleGenerateReport}
              disabled={!file || loading}
              startIcon={loading ? <CircularProgress size={16} /> : <FileText size={16} />}
            >
              {loading ? 'Generating Report...' : 'Generate Passing Targets Report'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Report Results */}
      {reportData && (
        <>
          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setReportData(null);
                setFile(null);
              }}
            >
              Generate New Report
            </Button>
            <Button
              variant="contained"
              onClick={saveReportAsPDF}
              startIcon={<FileText size={16} />}
            >
              Save as PDF
            </Button>
          </Box>

          <Box id="passing-targets-report-content">
            {/* Report Header */}
            <ReportHeader 
              title="PASSING TARGETS ANALYSIS"
              fileName={file?.name}
            />
            
            {/* Summary Stats */}
            <Card sx={{ mb: 4 }} data-section="summary-section">
              <CardContent>
                <Typography variant="h5" gutterBottom>Passing Summary</Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h3" color="primary.main">
                        {reportData.totalPassPlays}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Pass Plays
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h3" color="warning.main">
                        {reportData.totalDeepBalls}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Deep Balls ({reportData.deepBallPercentage}%)
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h3" color="success.main">
                        {reportData.receiverAnalysis.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Top Receivers Analyzed
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Receiver Analysis */}
            <Card sx={{ mb: 4 }} data-section="receivers-section">
              <CardContent>
                <Typography variant="h5" gutterBottom>Top 5 Receivers Analysis</Typography>
                {renderReceiverAnalysis(reportData.receiverAnalysis)}
              </CardContent>
            </Card>

            {/* Key Insights */}
            <Card sx={{ mb: 4 }} data-section="insights-section">
              <CardContent>
                <Typography variant="h5" gutterBottom>Key Insights</Typography>
                {renderInsights(reportData.insights)}
              </CardContent>
            </Card>
          </Box>
        </>
      )}

      {/* Instructions */}
      <Card sx={{ mt: 4, bgcolor: 'background.default' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Required CSV Columns</Typography>
          <Typography variant="body2" component="div">
            <ul>
              <li><strong>pff_RUNPASS:</strong> Contains 'P' for pass plays</li>
              <li><strong>pff_DEEPPASS:</strong> Identifies deep ball attempts (any value indicates deep ball)</li>
              <li><strong>pff_PASSRECEIVERTARGET:</strong> Target receiver identification</li>
            </ul>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PassingTargetsReportGenerator;